import React, { useState } from 'react';
import { FileText, Download, Check, Eye } from 'lucide-react';
import { sendLeaseSignedEmail } from '../utils/emailService';
import SignaturePad from '../components/SignaturePad';
import LeasePreview from '../components/LeasePreview';
import { generateLeaseAgreement, type StudentType, type LeaseTerms } from '../utils/leaseTemplates';
import { generateLeasePDF } from '../utils/leasePdfGenerator';

interface LeaseFormData {
  studentName: string;
  studentId: string;
  roomNumber: string;
  buildingName: string;
  startDate: string;
  endDate: string;
  monthlyRent: number;
  depositAmount: number;
  studentType: StudentType;
  terms: LeaseTerms;
}

const LeaseAgreement = () => {
  const [signed, setSigned] = useState(false);
  const [signature, setSignature] = useState<string>('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [leaseContent, setLeaseContent] = useState<{ coverPage: string; mainContent: string }>({ coverPage: '', mainContent: '' });
  const [acceptanceDate, setAcceptanceDate] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const logo = localStorage.getItem('companyLogo');

  // Mock data - in a real app, this would come from your backend
  const leaseData: LeaseFormData = {
    studentName: 'John Doe',
    studentId: '12345',
    roomNumber: '304',
    buildingName: 'Building B',
    startDate: '2024-01-01',
    endDate: '2024-12-31',
    monthlyRent: 8500,
    depositAmount: 8500,
    studentType: 'undergraduate',
    terms: 'annual',
  };

  React.useEffect(() => {
    // Generate lease content when component mounts
    const content = generateLeaseAgreement(leaseData);
    setLeaseContent(content);
  }, []);

  const handleSignatureSave = (signatureData: string) => {
    setSignature(signatureData);
  };

  const handleSign = async () => {
    if (!signature) {
      alert('Please provide your signature');
      return;
    }

    if (!termsAccepted) {
      alert('Please accept the terms of the lease agreement');
      return;
    }

    setIsProcessing(true);

    try {
      const currentDate = new Date().toISOString();
      setAcceptanceDate(currentDate);
      
      // Generate PDF with signature to verify it works
      await generateLeasePDF(leaseContent, {
        ...leaseData,
        signature,
        acceptanceDate: currentDate,
      });

      // Send email notification
      await sendLeaseSignedEmail('student@example.com', leaseData.studentName);
      
      setSigned(true);
      alert('Lease agreement signed successfully! You can now download the signed PDF.');
    } catch (error) {
      console.error('Error signing lease:', error);
      alert('Error signing lease agreement. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = async () => {
    if (!signed || !signature || !acceptanceDate) {
      alert('Please sign the lease agreement first');
      return;
    }

    setIsProcessing(true);

    try {
      const pdfBlob = await generateLeasePDF(leaseContent, {
        ...leaseData,
        signature,
        acceptanceDate,
      });

      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `lease_agreement_${leaseData.studentId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-6">
          {logo && <img src={logo} alt="Company Logo" className="h-12" />}
          <h1 className="text-2xl font-bold text-gray-800">Lease Agreement</h1>
        </div>

        <div className="mb-6">
          <FileText className="w-12 h-12 text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600 text-center">
            Please review and sign the lease agreement below
          </p>
        </div>

        <div className="border rounded-lg p-4 mb-6 bg-gray-50">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Agreement Content</h3>
            <button
              onClick={() => setIsPreviewOpen(true)}
              className="flex items-center px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <Eye className="w-4 h-4 mr-2" />
              Preview
            </button>
          </div>
          <pre className="whitespace-pre-wrap font-sans text-sm max-h-60 overflow-y-auto">
            {leaseContent.coverPage}
            {'\n\n'}
            {leaseContent.mainContent}
          </pre>
        </div>

        <div className="space-y-6">
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold mb-4">Digital Signature</h3>
            <SignaturePad onSave={handleSignatureSave} />
          </div>

          <div className="flex items-center mb-4">
            <input
              type="checkbox"
              id="terms"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              className="mr-2"
            />
            <label htmlFor="terms" className="text-gray-700">
              I have read and accept the terms of the lease agreement
            </label>
          </div>

          <div className="flex justify-between">
            <button
              className={`flex items-center px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed`}
              onClick={handleDownload}
              disabled={!signed || isProcessing}
            >
              <Download className="w-4 h-4 mr-2" />
              {isProcessing ? 'Processing...' : 'Download PDF'}
            </button>
            <button
              className={`flex items-center px-4 py-2 rounded ${
                signed
                  ? "bg-green-600 text-white"
                  : termsAccepted && signature
                  ? "bg-blue-600 text-white hover:bg-blue-700"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
              } disabled:opacity-50 disabled:cursor-not-allowed`}
              onClick={handleSign}
              disabled={!termsAccepted || !signature || signed || isProcessing}
            >
              {isProcessing ? (
                'Processing...'
              ) : signed ? (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Signed
                </>
              ) : (
                'Sign Agreement'
              )}
            </button>
          </div>
        </div>
      </div>

      <LeasePreview
        content={leaseContent}
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        leaseData={leaseData}
      />
    </div>
  );
};

export default LeaseAgreement;